class PageFeedback extends HTMLElement {
  static radioOptions = {
    yes: [
      'I found what I was looking for',
      'It was easy to understand',
      'Other',
    ],
    no: [
      "I couldn't find what I was looking for",
      'It was hard to understand',
      'The information was outdated',
      'Other',
    ],
  };

  constructor() {
    super();
    this._state = 'idle';
    this._vote = null;
  }

  connectedCallback() {
    this.render();
  }

  get basinUrl() {
    return this.getAttribute('basin-url') || '';
  }

  get basinFollowupUrl() {
    return this.getAttribute('basin-followup-url') || '';
  }

  get pageUrl() {
    return this.getAttribute('page-url') || window.location.href;
  }

  get pageTitle() {
    return this.getAttribute('page-title') || '';
  }

  setState(newState) {
    this._state = newState;
    this.render();
  }

  handleVote(vote) {
    const isFirstVote = this._state === 'idle';
    this._vote = vote;
    this._state = 'voted';

    // Only submit to Basin on the first vote
    if (isFirstVote && this.basinUrl) {
      const formData = new FormData();
      formData.append('vote', vote);
      formData.append('page_url', this.pageUrl);
      formData.append('page_title', this.pageTitle);
      fetch(this.basinUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      }).catch(() => {});
    }

    // Mark the active vote button
    this.querySelectorAll('.pf-vote-btn').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.vote === vote);
    });

    // Populate followup content
    const inner = this.querySelector('.pf-followup-inner');
    if (inner) {
      inner.innerHTML = this.getFollowupHTML();
      this.bindFormEvents();
    }

    if (isFirstVote) {
      this.revealFollowup();
    }
  }

  revealFollowup() {
    const followup = this.querySelector('.pf-followup');
    if (!followup) return;

    requestAnimationFrame(() => {
      followup.classList.add('is-open');
      followup.addEventListener('transitionend', () => {
        followup.classList.add('is-visible');
        const fieldset = this.querySelector('fieldset');
        if (fieldset) fieldset.focus();
      }, { once: true });
    });
  }

  async handleSubmit(e) {
    e.preventDefault();
    const form = e.target;

    // Validate a radio is selected
    const hasReason = form.querySelector('input[name="reason"]:checked');
    const errorEl = this.querySelector('.pf-field-error');
    if (!hasReason) {
      if (errorEl) errorEl.hidden = false;
      return;
    }
    if (errorEl) errorEl.hidden = true;

    const formData = new FormData(form);
    formData.append('vote', this._vote);
    formData.append('page_url', this.pageUrl);
    formData.append('page_title', this.pageTitle);

    this._state = 'submitting';

    // 1. Fade out followup content, then collapse it
    const followup = this.querySelector('.pf-followup');
    if (followup) {
      followup.classList.remove('is-visible');
      await this.onTransitionEnd(followup, 300);
      followup.classList.remove('is-open');
      await this.onTransitionEnd(followup, 400);
    }

    // 2. Crossfade question row to thank you message
    const questionRow = this.querySelector('.pf-question-row');
    if (questionRow) {
      questionRow.classList.add('is-fading');
      await this.onTransitionEnd(questionRow, 300);
      questionRow.innerHTML = `<p class="pf-submitted-message">Thank you for helping us improve this page.</p>`;
      requestAnimationFrame(() => questionRow.classList.remove('is-fading'));
    }

    // 3. Fire the request
    try {
      const response = await fetch(this.basinFollowupUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      if (!response.ok) throw new Error('Submission failed');
      this._state = 'submitted';
    } catch {
      this.setState('error');
    }
  }

  onTransitionEnd(el, fallbackMs) {
    return new Promise((resolve) => {
      const timer = setTimeout(resolve, fallbackMs + 50);
      el.addEventListener('transitionend', () => {
        clearTimeout(timer);
        resolve();
      }, { once: true });
    });
  }

  getFollowupHTML() {
    const options = PageFeedback.radioOptions[this._vote] || [];
    const radios = options
      .map(
        (label, i) => `
        <label class="pf-radio-label">
          <input class="pf-radio-input" type="radio" name="reason" value="${label}" ${i === 0 ? 'required' : ''}>
          <span class="pf-radio-circle"></span>
          ${label}
        </label>
      `
      )
      .join('');

    return `
      <p class="pf-thanks-message">Thanks for your feedback.</p>
      <form novalidate>
        <fieldset tabindex="-1">
          <legend>Tell us more:</legend>
          <div class="pf-radio-group">
            ${radios}
          </div>
          <p class="pf-field-error" hidden>Please select an option.</p>
        </fieldset>
        <div class="px-2">
          <textarea name="comments" placeholder="Additional comments (optional)"></textarea>
        </div>
        <div class="pf-btn-row">
          <button type="submit" class="pf-submit-btn">Submit</button>
        </div>
      </form>
    `;
  }

  renderQuestionRow() {
    return `
      <div class="pf-question-row">
        <p class="pf-question-text">Did you find this page useful?</p>
        <div class="pf-vote-buttons">
          <button type="button" class="pf-vote-btn" data-vote="yes">Yes</button>
          <button type="button" class="pf-vote-btn" data-vote="no">No</button>
        </div>
      </div>
      <div class="pf-followup">
        <div class="pf-followup-inner"></div>
      </div>
    `;
  }

  renderError() {
    return `
      <p class="pf-error-message">Something went wrong. Please try again.</p>
      <button type="button" class="pf-retry-btn">Retry</button>
    `;
  }

  render() {
    let content;
    switch (this._state) {
      case 'idle':
      case 'voted':
        content = this.renderQuestionRow();
        break;
      case 'error':
        content = this.renderError();
        break;
      default:
        return;
    }

    this.innerHTML = content;
    this.bindEvents();

    // Re-entering voted state (e.g. retry) — repopulate and animate the followup
    if (this._state === 'voted') {
      const inner = this.querySelector('.pf-followup-inner');
      if (inner) {
        inner.innerHTML = this.getFollowupHTML();
        this.bindFormEvents();
      }
      this.querySelectorAll('.pf-vote-btn').forEach((btn) => {
        btn.classList.toggle('is-active', btn.dataset.vote === this._vote);
      });
      this.revealFollowup();
    }
  }

  bindEvents() {
    this.querySelectorAll('.pf-vote-btn[data-vote]').forEach((btn) => {
      if (!btn.disabled) {
        btn.addEventListener('click', () => this.handleVote(btn.dataset.vote));
      }
    });

    this.bindFormEvents();

    const retryBtn = this.querySelector('.pf-retry-btn');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => this.setState('voted'));
    }
  }

  bindFormEvents() {
    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', (e) => this.handleSubmit(e));

      // Clear validation error when a radio is selected
      form.querySelectorAll('input[name="reason"]').forEach((radio) => {
        radio.addEventListener('change', () => {
          const errorEl = this.querySelector('.pf-field-error');
          if (errorEl) errorEl.hidden = true;
        });
      });
    }
  }
}

customElements.define('page-feedback', PageFeedback);
